import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCode, faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import RouteSeo from '../components/RouteSeo';
import ToolContent from '../components/ToolContent';
import ToolLayout from '../components/ToolLayout';
import { BASE64_ABOUT } from '../content/toolGuides';
import { BASE64_ROUTE } from '../content/tools';
import { useTextConverter } from '../hooks/useTextConverter';
import { encodeBase64, decodeBase64 } from '../utils/base64';

const Base64Encoding: React.FC = () => {
  const converter = useTextConverter({
    modes: ['encode', 'decode'] as const,
    transforms: { encode: encodeBase64, decode: decodeBase64 },
    errors: {
      encode: 'Failed to encode the text.',
      decode: 'Failed to decode. Make sure your input is valid Base64.',
    },
  });
  const { mode } = converter;

  return (
    <>
      <RouteSeo route={BASE64_ROUTE} />
      <ToolLayout
        title="Base64 Encoding / Decoding"
        description="Encode or decode text using Base64 encoding"
        icon={faCode}
        controls={
          <div className="tool-controls__group">
            <Form.Check
              type="radio"
              id="encode-radio"
              label="Encode"
              checked={mode === 'encode'}
              onChange={() => {
                converter.setMode('encode');
                converter.clearError();
              }}
              inline
            />
            <Form.Check
              type="radio"
              id="decode-radio"
              label="Decode"
              checked={mode === 'decode'}
              onChange={() => {
                converter.setMode('decode');
                converter.clearError();
              }}
              inline
            />
          </div>
        }
        error={converter.error}
        onDismissError={converter.clearError}
        inputLabel={mode === 'encode' ? 'Plain Text' : 'Base64 Text'}
        inputValue={converter.input}
        onInputChange={converter.setInput}
        inputPlaceholder={mode === 'encode' ? 'Enter text to encode...' : 'Enter Base64 to decode...'}
        actions={
          <>
            <Button variant="primary" onClick={converter.convert} disabled={!converter.input}>
              <FontAwesomeIcon icon={faArrowRight} className="me-2" />
              {mode === 'encode' ? 'Encode' : 'Decode'}
            </Button>
            <Button variant="outline-secondary" onClick={converter.switchMode} disabled={!converter.output}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Switch
            </Button>
          </>
        }
        outputLabel={mode === 'encode' ? 'Base64 Text' : 'Plain Text'}
        outputValue={converter.output}
        onCopy={converter.copy}
        copied={converter.copied}
        aboutTitle="About Base64 Encoding"
        about={BASE64_ABOUT}
      >
        <ToolContent route={BASE64_ROUTE} />
      </ToolLayout>
    </>
  );
};

export default Base64Encoding;
